import React, { useState } from 'react';
import { Form, Select, DatePicker, Upload, Button, Space, message } from 'antd';
import { Staff } from '../../services/staffs';
import { UploadOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

interface User {
  id: number;
  name: string;
  email: string;
}

interface EngagementTabProps {
  form: any;
  staff: User[];
  teams?: any[];
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  team?: { id: number; name: string } | null;
  reportingFiles?: {
    tax?: UploadFile[];
  };
  onFileChange?: (field: string, files: UploadFile[]) => void;
  customerId?: number;
  onEngagementDateChange?: () => void;
}

interface EngagementRow {
  key: string;
  label: string;
  staffField: string;
  appointDateField: string;
  resignDateField: string;
  attachmentField?: string;
  hasAttachment: boolean;
}

export const EngagementTab: React.FC<EngagementTabProps> = ({
  form,
  staff,
  teams = [],
  isEditing = false,
  isCustomerPortal = false,
  team = null,
  reportingFiles = {},
  onFileChange,
  customerId,
  onEngagementDateChange,
}) => {
  const canEdit = isEditing && !isCustomerPortal;
  const [fileActionLoading, setFileActionLoading] = useState<Record<string, { view?: boolean; download?: boolean }>>({});

  const setFileLoading = (field: string, action: 'view' | 'download', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [action]: isLoading,
      },
    }));
  };

  const handleView = async (field: string, file: UploadFile) => {
    if (!file || !customerId) return;
    
    try {
      setFileLoading(field, 'view', true);
      // If it's a new file (has originFileObj), use it directly
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const newWindow = window.open(blobUrl, '_blank');

        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 60000); // 60 seconds
      } else {
        // For existing files from S3, fetch via API
        const isCustomerPortalPath = window.location.pathname === '/customer'
          || window.location.pathname.startsWith('/customer/');
        let url: string;
        let apiClient: any;
        
        if (isCustomerPortalPath) {
          url = `/customer/supporting-files/${customerId}/${field}/view`;
          apiClient = customerApi;
        } else {
          url = `/customers/${customerId}/supporting-files/${field}/view`;
          apiClient = api;
        }
        
        const response = await apiClient.get(url, {
          responseType: 'blob',
        });
        
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/octet-stream' 
        });
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (error: any) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const handleDownload = async (field: string, file: UploadFile) => {
    if (!file || !customerId) return;
    
    try {
      setFileLoading(field, 'download', true);
      // If it's a new file (has originFileObj), use it directly
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.name || field;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } else {
        // For existing files from S3, fetch via API
        const isCustomerPortalPath = window.location.pathname === '/customer'
          || window.location.pathname.startsWith('/customer/');
        let url: string;
        let apiClient: any;
        
        if (isCustomerPortalPath) {
          url = `/customer/supporting-files/${customerId}/${field}/download`;
          apiClient = customerApi;
        } else {
          url = `/customers/${customerId}/supporting-files/${field}/download`;
          apiClient = api;
        }
        
        const response = await apiClient.get(url, {
          responseType: 'blob',
        });
        
        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/octet-stream' 
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.name || field;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      }
    } catch (error: any) {
      message.error('Failed to download file');
    } finally {
      setFileLoading(field, 'download', false);
    }
  };

  const renderAttachmentField = (field: string, files?: UploadFile[]) => {
    if (canEdit) {
      return (
        <div>
          <Upload
            fileList={files || []}
            beforeUpload={() => false}
            onChange={(info) => {
              if (onFileChange) {
                onFileChange(field, info.fileList);
              }
            }}
            maxCount={1}
            accept=".pdf,.png,.jpg,.jpeg"
          >
            <Button size="small" icon={<UploadOutlined />} style={{ width: '100%' }}>
              Upload
            </Button>
          </Upload>
          {files && files.length > 0 && (
            <Space style={{ marginTop: 8 }} size="small">
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={fileActionLoading[field]?.view}
                onClick={() => {
                  const file = files[0];
                  if (file && customerId) {
                    handleView(field, file);
                  }
                }}
              >
                View
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={fileActionLoading[field]?.download}
                onClick={() => {
                  const file = files[0];
                  if (file && customerId) {
                    handleDownload(field, file);
                  }
                }}
              >
                Download
              </Button>
            </Space>
          )}
        </div>
      );
    } else {
      return (
        <div>
          {files && files.length > 0 ? (
            <Space size="small">
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={fileActionLoading[field]?.view}
                onClick={() => {
                  const file = files[0];
                  if (file && customerId) {
                    handleView(field, file);
                  }
                }}
              >
                View
              </Button>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={fileActionLoading[field]?.download}
                onClick={() => {
                  const file = files[0];
                  if (file && customerId) {
                    handleDownload(field, file);
                  }
                }}
              >
                Download
              </Button>
            </Space>
          ) : (
            <div style={{ color: '#999', fontSize: '12px' }}>No attachment</div>
          )}
        </div>
      );
    }
  };

  const engagementRows: EngagementRow[] = [
    {
      key: 'advisor',
      label: 'Advisor',
      staffField: 'agent_staff_id',
      appointDateField: 'agent_appoint_date',
      resignDateField: 'agent_resign_date',
      hasAttachment: false,
    },
    {
      key: 'tax',
      label: 'Tax',
      staffField: 'staff_id',
      appointDateField: 'appoint_date',
      resignDateField: 'resign_date',
      attachmentField: 'tax',
      hasAttachment: true,
    },
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', width: '150px', borderRight: '1px solid #f0f0f0' }}></th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Staff</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Appoint Date</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Resign Date</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa' }}>Attachment</th>
          </tr>
        </thead>
        <tbody>
          {engagementRows.map((row) => (
            <tr key={row.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', fontWeight: 600, backgroundColor: '#fafafa', width: '150px', borderRight: '1px solid #f0f0f0' }}>
                {row.label}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
                {canEdit ? (
                  <Form.Item name={row.staffField} style={{ margin: 0 }}>
                    <Select allowClear placeholder="Select staff" style={{ width: '100%' }}>
                      {staff.map((staffItem) => (
                        <Select.Option key={staffItem.id} value={staffItem.id}>
                          {staffItem.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <div>
                    {(() => {
                      const staffId = form.getFieldValue(row.staffField);
                      const foundStaff = staff.find(s => s.id === staffId);
                      return foundStaff?.name || '-';
                    })()}
                  </div>
                )}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
                {canEdit ? (
                  <Form.Item 
                    name={row.appointDateField} 
                    style={{ margin: 0 }}
                    dependencies={[row.resignDateField]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        // Ensure date is set to start of day to avoid timezone issues
                        if (date) {
                          form.setFieldValue(row.appointDateField, date.startOf('day'));
                        } else {
                          form.setFieldValue(row.appointDateField, null);
                        }
                        if (onEngagementDateChange) {
                          onEngagementDateChange();
                        }
                      }}
                    />
                  </Form.Item>
                ) : (
                  <div>
                    {(() => {
                      const appointDate = form.getFieldValue(row.appointDateField);
                      if (appointDate) {
                        return dayjs(appointDate).format('DD/MM/YYYY');
                      }
                      return '-';
                    })()}
                  </div>
                )}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
                {canEdit ? (
                  <Form.Item 
                    name={row.resignDateField} 
                    style={{ margin: 0 }}
                    dependencies={[row.appointDateField]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        // Ensure date is set to start of day to avoid timezone issues
                        if (date) {
                          form.setFieldValue(row.resignDateField, date.startOf('day'));
                        } else {
                          form.setFieldValue(row.resignDateField, null);
                        }
                        if (onEngagementDateChange) {
                          onEngagementDateChange();
                        }
                      }}
                    />
                  </Form.Item>
                ) : (
                  <div>
                    {(() => {
                      const resignDate = form.getFieldValue(row.resignDateField);
                      if (resignDate) {
                        return dayjs(resignDate).format('DD/MM/YYYY');
                      }
                      return '-';
                    })()}
                  </div>
                )}
              </td>
              <td style={{ padding: '12px' }}>
                {row.hasAttachment ? (
                  renderAttachmentField(row.attachmentField!, reportingFiles[row.attachmentField as keyof typeof reportingFiles])
                ) : (
                  <div style={{ color: '#999', fontSize: '12px' }}>-</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
